import ExpoConstants from 'expo-constants';
import axios, { isAxiosError, type AxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { createAuthRefresh } from 'axios-auth-refresh';
import i18n from 'i18next';
import { authEventBus } from 'src/core/events/Events';
import type { EventAuthOnLogOut } from 'src/core/events/EventTypes';
import Constants from 'src/core/utils/Constants';
import { apiToken, apiUrl } from 'src/core/utils/AppConfig';
import type { ApiReservationId } from 'src/core/models/api/ApiReservation';
import type ApiSettings from 'src/core/models/api/ApiSettings';
import type ApiResourceResponse from 'src/core/models/api/responses/ApiResourceResponse';
import type ApiCreateOneTimePasswordResponse from 'src/core/models/api/responses/ApiCreateOneTimePasswordResponse';
import type ApiWorkspaceDetailResponse from 'src/core/models/api/responses/ApiWorkspaceDetailResponse';
import type ApiWorkspacesResponse from 'src/core/models/api/responses/ApiWorkspacesResponse';
import type ApiWelcomeResponse from 'src/core/models/api/responses/ApiWelcomeResponse';
import type ApiBathCardsResponse from 'src/core/models/api/responses/ApiBathCardsResponse';
import type ApiContentInspirationResponse from 'src/core/models/api/responses/ApiContentInspirationResponse';
import type ApiContentTutorialResponse from 'src/core/models/api/responses/ApiContentTutorialResponse';
import type ApiErrorResponse from 'src/core/models/api/responses/ApiErrorResponse';
import type ApiLoginResponse from 'src/core/models/api/responses/ApiLoginResponse';
import type ApiLoyaltyResponse from 'src/core/models/api/responses/ApiLoyaltyResponse';
import type ApiMeResponse from 'src/core/models/api/responses/ApiMeResponse';
import type ApiMessageDetailResponse from 'src/core/models/api/responses/ApiMessageDetailResponse';
import type ApiMessagesResponse from 'src/core/models/api/responses/ApiMessagesResponse';
import type ApiPreferencesResponse from 'src/core/models/api/responses/ApiPreferencesResponse';
import type ApiProfileResponse from 'src/core/models/api/responses/ApiProfileResponse';
import type ApiReservationsResponse from 'src/core/models/api/responses/ApiReservationsResponse';
import CrashlyticsService from 'src/data/services/CrashlyticsService';
import StorageService from 'src/data/services/StorageService';

type CustomAxiosRequestConfig = AxiosRequestConfig<unknown> & { skipAuthRefresh?: boolean };

const TAG = 'API ->';

const skipAuthRefreshConfig: CustomAxiosRequestConfig = { skipAuthRefresh: true };

const authorizationConfig = (accessToken: string): CustomAxiosRequestConfig => ({
	skipAuthRefresh: true,
	headers: { [Constants.apiHeaderAuthorization]: `Bearer ${accessToken}` },
});

const API_BASE_URL = apiUrl();

const apiClient = axios.create({
	...(API_BASE_URL ? { baseURL: API_BASE_URL } : {}),
	headers: {
		[Constants.apiHeaderContentType]: Constants.apiHeaderValueApplicationJson,
		[Constants.apiHeaderAccept]: Constants.apiHeaderValueApplicationJson,
		[Constants.apiHeaderXChannel]: Constants.apiChannel,
		[Constants.apiHeaderXToken]: apiToken(),
		[Constants.apiHeaderXAppVersion]: ExpoConstants.expoConfig?.version,
	},
	timeout: Constants.apiTimeout,
});

apiClient.interceptors.request.use(async (request) => {
	const token = await StorageService.getAccessToken();

	if (token) {
		request.headers.set(Constants.apiHeaderAuthorization, `Bearer ${token}`);
		request.headers.delete(Constants.apiHeaderAcceptLanguage);
	}

	request.headers.set(Constants.apiHeaderAcceptLanguage, i18n.language);

	return request;
});

apiClient.interceptors.response.use(
	(res) => res,
	(error: AxiosError<ApiErrorResponse>) => {
		const config = error.response?.config;
		const status = error.response?.status;
		const data = error.response?.data;

		if (status) {
			const urlString = (config?.baseURL ?? '') + config?.url?.toString();
			console.warn(`${TAG} URL:${urlString} ERROR:${error} DATA:${data?.message ?? '-'}`);
		}

		return Promise.reject(error);
	},
);

let refreshInFlight: Promise<void> | null = null;

const refreshAccessToken = async () => {
	const response = await auth.refreshToken();
	const currentAccessToken = await StorageService.getAccessToken();
	const newToken = response.data.authorization.access_token;

	if (newToken && newToken !== currentAccessToken) {
		await StorageService.setAccessToken(newToken);
	}
};

const isSessionRejected = (error: unknown) =>
	isAxiosError(error) && error.response?.status === Constants.httpStatusUnauthorized;

createAuthRefresh(
	apiClient,
	async () => {
		refreshInFlight ??= refreshAccessToken().finally(() => {
			refreshInFlight = null;
		});

		try {
			return await refreshInFlight;
		} catch (error) {
			console.warn(`${TAG} FAIL refreshToken() ->`, error);

			if (isSessionRejected(error)) {
				authEventBus.emit(Constants.eventAuthOnLogOut, { forceLogin: true } satisfies EventAuthOnLogOut);
			} else {
				CrashlyticsService.captureEvent({
					message: 'Refreshing the access token failed.',
					extra: { error },
					reportHandledStatuses: true,
				});
			}

			return await Promise.reject(error);
		}
	},
	{ deduplicateRefresh: false },
);

const response = <T>(response: AxiosResponse<T>) => response;

type GetConfig = AxiosRequestConfig<unknown> & { group?: string };

type InFlightGet = { identity: string; controller: AbortController; promise: Promise<unknown> };

const inFlightGets = new Map<string, InFlightGet>();

const isTokenScoped = (config?: AxiosRequestConfig<unknown>) =>
	config?.headers?.[Constants.apiHeaderAuthorization] !== undefined;

const get = <T>(url: string, config?: GetConfig) => {
	const { group, ...axiosConfig } = config ?? {};

	if (isTokenScoped(axiosConfig)) {
		return apiClient.get<T>(url, axiosConfig).then(response);
	}

	const slot = group ?? url;
	const running = inFlightGets.get(slot);

	if (running?.identity === url) {
		return running.promise as Promise<AxiosResponse<T>>;
	}

	running?.controller.abort();

	const controller = new AbortController();
	const promise = apiClient
		.get<T>(url, { ...axiosConfig, signal: controller.signal })
		.then(response)
		.finally(() => {
			if (inFlightGets.get(slot)?.controller === controller) {
				inFlightGets.delete(slot);
			}
		});

	inFlightGets.set(slot, { identity: url, controller, promise });

	return promise;
};

const request = {
	get,
	post: <T>(url: string, body: object, config?: AxiosRequestConfig<unknown> | undefined) =>
		apiClient.post<T>(url, body, config).then(response),
	put: <T>(url: string, body?: object) => apiClient.put<T>(url, body).then(response),
	patch: <T>(url: string, body: object) => apiClient.patch<T>(url, body).then(response),
	delete: <T>(url: string, config?: AxiosRequestConfig<unknown> | undefined) =>
		apiClient.delete<T>(url, config).then(response),
};

const settings = {
	get: () => request.get<ApiSettings>('/my/settings'),
};

const auth = {
	checkEmail: (email: string) => request.post('/auth/member/check-email', { email }, skipAuthRefreshConfig),
	registerAccount: (data: Record<string, unknown>) => request.post('/auth/member/register', data, skipAuthRefreshConfig),
	refreshToken: () => request.post<ApiLoginResponse>('/auth/member/refresh', {}, skipAuthRefreshConfig),
	login: (email: string, password: string, device_name: string) =>
		request.post<ApiLoginResponse>('/auth/member/login', { email, password, device_name }, skipAuthRefreshConfig),
	logout: (accessToken: string) => request.post('/auth/member/logout', {}, authorizationConfig(accessToken)),
	me: (accessToken?: string) =>
		request.get<ApiMeResponse>('/auth/member/me', accessToken ? authorizationConfig(accessToken) : undefined),
	magicLink: (email: string) => request.post('/auth/member/magic-link', { email }, skipAuthRefreshConfig),
	verifyMagicLink: (token: string) =>
		request.get<ApiLoginResponse>(`/auth/member/magic-link/${token}`, skipAuthRefreshConfig),
	resendVerificitation: (email: string) =>
		request.post('/auth/member/resend-email-verification', { email }, skipAuthRefreshConfig),
	resetPassword: (email: string) => request.post('/auth/member/reset-password', { email }, skipAuthRefreshConfig),
	forgotPassword: (email: string) => request.post('/auth/member/forgot-password', { email }, skipAuthRefreshConfig),
	delete: (accessToken: string, password: string) =>
		request.post('/auth/member/delete', { password }, authorizationConfig(accessToken)),
	createOneTimePassword: (type: ApiCreateOneTimePasswordResponse['type']) =>
		request.post<ApiCreateOneTimePasswordResponse>(`/auth/member/one-time-password/${type}`, {}),
};

const bathcards = {
	get: () => request.get<ApiBathCardsResponse>('/my/bath-cards'),
	delete: (cardId: string) => request.delete(`/my/bath-cards?${Constants.paramsBathCardId}=${cardId}`),
	add: (workspace_id: string, barcode: string) =>
		request.post<ApiBathCardsResponse>('/my/bath-cards', { workspace_id, barcode }),
};

const loyalty = {
	get: () => request.get<ApiLoyaltyResponse>('/my/loyalty'),
};

const inspiration = {
	get: () => request.get<ApiContentInspirationResponse>('/my/inspiration'),
};

const tutorial = {
	get: () => request.get<ApiContentTutorialResponse>('/my/tutorial'),
};

const messages = {
	get: () => request.get<ApiMessagesResponse>('/my/messages'),
	find: (message_id: string) =>
		request.get<ApiMessageDetailResponse>(`/my/messages/${message_id}?${Constants.paramsMessageId}=${message_id}`),
	read: (message_id: string) =>
		request.put(`/my/messages/${message_id}/read?${Constants.paramsMessageId}=${message_id}`),
	send: (message: string, subject: string, workspaceId: string, reservationNumber: string | null) =>
		request.post('/my/messages/send', {
			message,
			subject,
			workspace: workspaceId,
			reservationNumber,
		}),
};

const preferences = {
	get: () => request.get<ApiPreferencesResponse>('/my/preferences'),
	setPreferredLanguage: (id: string) => request.put('/my/preferences/preferred_language', { id }),
	setEmailNotificationPreference: (id: string, selected: boolean) =>
		request.put('/my/preferences/email_notification', { id, selected }),
	setAppNotificationPreference: (id: string, selected: boolean) =>
		request.put('/my/preferences/app_notification', { id, selected }),
};

const profile = {
	get: () => request.get<ApiProfileResponse>('/my/profile'),
	setAddress: (data: Record<string, unknown>) => request.put<ApiProfileResponse>('/my/profile/address', data),
	setPersonalData: (data: Record<string, unknown>) => request.put<ApiProfileResponse>('/my/profile/personal', data),
};

const reservations = {
	find: <DataT>(id: ApiReservationId) =>
		request.get<ApiResourceResponse<DataT>>(`/my/reservations/${id}?${Constants.paramsReservationId}=${id}`),
	reply: (reservation_id: ApiReservationId, reply_message: string) =>
		request.post(`/my/reservations/${reservation_id}/reply?${Constants.paramsReservationId}=${reservation_id}`, {
			reply_message,
		}),
	getHistorical: () =>
		request.get<ApiReservationsResponse>(`/my/reservations?${Constants.paramsFilterHistorical}=true`, {
			timeout: Constants.apiTimeoutReservations,
		}),
	getUpcoming: () =>
		request.get<ApiReservationsResponse>(`/my/reservations?${Constants.paramsFilterUpcoming}=true`, {
			timeout: Constants.apiTimeoutReservations,
		}),
	addManually: (workspace_id: string, reservation_number: string) =>
		request.post('/my/reservations/add-manually', { workspace_id, reservation_number }),
};

const welcome = {
	get: () => request.get<ApiWelcomeResponse>('/my/welcome'),
};

const workspaces = {
	get: () => request.get<ApiWorkspacesResponse>('/my/workspaces'),
	detail: (workspaceId: number) => request.get<ApiWorkspaceDetailResponse>(`/my/workspaces/${workspaceId}`),
};

const ApiService = {
	auth,
	settings,
	bathcards,
	loyalty,
	inspiration,
	tutorial,
	messages,
	preferences,
	profile,
	reservations,
	welcome,
	workspaces,
};

export default ApiService;
